import React, { useEffect, useState } from 'react';
import { Drawer, Layout } from 'antd';
import styled from 'styled-components';
import { CustomImage, Text } from '@src/components';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import setting from '@src/assets/icons/icon-setting.svg';
import notification from '@src/assets/icons/icon-notification.svg';
import exit from '@src/assets/icons/icon-exit.svg';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '@src/store/slice/authSlice';
import Headers from '@src/components/header';
import Menus from '@src/components/Menus';
import Footer from '@src/components/footer';
import GoogleAndSmplComponent from '@src/components/GoogleAndSmpl';
import useErrorBoundary from 'use-error-boundary';

const { Content, Sider } = Layout;

const CustomSider = styled(Sider)(
  () => `
      background-color: #fff;
      box-shadow: 0 2px 7px 0 rgb(0 0 0 / 9%) !important;
      overflow: auto,
      position: fixed,
      left: 0,
      top: 0,
      bottom: 0,
      
     ::-webkit-scrollbar-track {
        background-color: #fff;
     }
     
     ::-webkit-scrollbar {
        width: 0;
        height:100%;
      }
      
      ::-webkit-scrollbar-thumb {
        box-shadow: inset 20px 0px 7px 1px rgb(0 0 0 / 9%) !important;
      }
      
`,
  `
    margin-top: 100px;

  > div {
     display: flex;
     flex-direction: column;
     justify-content:space-between;
  }
`,
);

const StyledContent = styled(Content)`
  padding: 18px 48px !important;

  @media (max-width: 767.98px) {
    padding: 18px 18px 32px 18px !important;
    min-height: calc(100vh - 80px);
    margin-top: 80px;
  }

  @media (min-width: 768px) and (max-width: 991.98px) {
    padding: 20px 36px;
    min-height: calc(100vh - 80px);
  }

  @media (min-width: 992px) {
    padding: 0 48px;
    min-height: calc(100vh - 100px);
    margin-top: 100px;
  }
`;

const CustomDrawer = styled(Drawer)`
  top: 80px;
  height: calc(100vh - 80px);

  .ant-drawer-body {
    padding: 0;
    background-color: #fff;
    overflow-x: hidden;
  }

  .ant-drawer-footer {
    height: 48px;
  }
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CustomLayout = styled(Layout)(({ isdesktop, fullWidth }) =>
  isdesktop === 'true' && !fullWidth
    ? `
     margin-left: 256px !important;
`
    : ``,
);

const ResponsiveLayout = ({ children }) => {
  const isDesktop = useMediaQuery({ query: '(min-width: 992px)' });
  const [collapsed, setCollapsed] = useState(false);
  const { isAdmin } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const { ErrorBoundary, didCatch, error } = useErrorBoundary();
  const { fullWidth } = useSelector((state) => state?.appSettings);

  useEffect(() => {
    if (!collapsed) return;
    const maskElement = document.querySelector('.ant-drawer-mask');
    if (maskElement) {
      maskElement.addEventListener('click', () => {
        setCollapsed(false);
      });
    }
    return () => {
      if (maskElement) {
        maskElement.removeEventListener('click', () => {
          setCollapsed(false);
        });
      }
    };
  }, [collapsed]);

  const isDesktopShow = () => {
    return isDesktop ? (
      <CustomSider
        width={256}
        collapsed={false}
        collapsedWidth={0}
        style={{
          overflow: 'auto',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: isAdmin ? '#3f4957' : '#fff',
        }}
      >
        <Menus />
        <Footer />
      </CustomSider>
    ) : (
      <CustomDrawer
        width={256}
        title={false}
        placement="left"
        visible={collapsed}
        closable={false}
        maskClosable={true}
        footer={
          <FooterContainer>
            <CustomImage src={setting} style={{ cursor: 'pointer' }} />

            <CustomImage src={notification} style={{ cursor: 'pointer' }} />

            <CustomImage
              src={exit}
              style={{ cursor: 'pointer' }}
              onClick={() => dispatch(logout())}
            />
          </FooterContainer>
        }
      >
        <Menus setCollapsed={setCollapsed} />
        <Footer />
      </CustomDrawer>
    );
  };

  return (
    <Layout>
      <Headers isDesktop={isDesktop} collapsed={collapsed} setCollapsed={setCollapsed} />
      {fullWidth ? null : isDesktopShow()}

      <CustomLayout fullWidth={fullWidth} isdesktop={isDesktop?.toString()}>
        <StyledContent>
          {didCatch ? (
            <p>
              <Text t="anErrorWasEncounteredRefreshTheScreen" /> <br />
              <p>
                <Text t="errorText" /> {error.message}
              </p>
            </p>
          ) : (
            <ErrorBoundary>{children}</ErrorBoundary>
          )}
        </StyledContent>
      </CustomLayout>
      <GoogleAndSmplComponent />
    </Layout>
  );
};

export default ResponsiveLayout;
